/**
 * TechTreePickerModal — "Search existing Tech Tree" on the Solutions page.
 *
 * Browse the Discipline → Technology tree (expand branches) or search it; pick a
 * technology and add it as a solution family anchored on that tech. Search is
 * client-side over the whole taxonomy (see techTreeSearch): the tree is pruned to
 * matches + their ancestors and auto-expanded, so a match shows where it lives.
 *
 * The page owns the add mutation and the taxonomy's families; this only knows
 * which techs already anchor a family (to badge them / disable re-adding).
 */
import { type ReactNode, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2, Plus, Search, X } from 'lucide-react'

import { cn } from '../../lib/utils'
import { tokenManager } from '../../lib/api'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Modal from '../../components/ui/Modal'
import type { FamilyStatus } from './SolutionFamilyDetailModal'
import {
  ancestorsOf,
  buildPickerTree,
  highlightRanges,
  searchPickerTree,
  tokenize,
  type PickerNode,
  type PickerTree,
  type PickerTreeResponse,
} from './techTreeSearch'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const STATUS_BADGE: Record<FamilyStatus, { label: string; variant: 'success' | 'default' | 'rejected' }> = {
  accepted: { label: 'Added', variant: 'success' },
  proposed: { label: 'Proposed', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'rejected' },
  superseded: { label: 'Superseded', variant: 'default' },
}

const Highlighted = ({ text, tokens }: { text: string; tokens: string[] }) => {
  const ranges = tokens.length ? highlightRanges(text, tokens) : []
  if (ranges.length === 0) return <>{text}</>
  const parts: ReactNode[] = []
  let at = 0
  ranges.forEach(([start, end], i) => {
    if (start > at) parts.push(text.slice(at, start))
    parts.push(
      <mark key={i} className="rounded-sm bg-primary/20 text-foreground">
        {text.slice(start, end)}
      </mark>,
    )
    at = end
  })
  if (at < text.length) parts.push(text.slice(at))
  return <>{parts}</>
}

interface TechTreePickerModalProps {
  isOpen: boolean
  onClose: () => void
  /** Status of the family anchored on each tech already in this taxonomy. */
  familyStatusByTechId: Map<number, FamilyStatus>
  onAdd: (technologyId: number) => Promise<void>
  /** Generation is running — browsing is fine, adding is not. */
  addDisabledReason?: string | null
}

const TechTreePickerModal = ({
  isOpen,
  onClose,
  familyStatusByTechId,
  onAdd,
  addDisabledReason,
}: TechTreePickerModalProps) => {
  const [tree, setTree] = useState<PickerTree | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // While searching, expansion defaults to "ancestors of matches"; these are the
  // user's flips on top of that, reset whenever the query changes.
  const [searchToggles, setSearchToggles] = useState<Set<string>>(new Set())
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [justAdded, setJustAdded] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Load the taxonomy once, the first time the picker opens.
  useEffect(() => {
    if (!isOpen || tree) return
    let cancelled = false
    setLoadError(null)
    const token = tokenManager.getAccessToken()
    fetch(`${API_BASE_URL}/api/technologies/picker-tree/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Tech tree fetch failed (${res.status})`)
        const data: PickerTreeResponse = await res.json()
        if (!cancelled) setTree(buildPickerTree(data))
      })
      .catch((e) => {
        if (!cancelled) setLoadError((e as Error).message)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, tree])

  useEffect(() => {
    if (isOpen) searchRef.current?.focus()
  }, [isOpen, tree])

  const tokens = useMemo(() => tokenize(deferredQuery), [deferredQuery])
  const result = useMemo(
    () => (tree ? searchPickerTree(tree, deferredQuery) : null),
    [tree, deferredQuery],
  )

  useEffect(() => setSearchToggles(new Set()), [deferredQuery])

  const isExpanded = (key: string) =>
    result ? result.expand.has(key) !== searchToggles.has(key) : expanded.has(key)

  const toggleExpand = (key: string) => {
    const flip = (prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    }
    if (result) setSearchToggles(flip)
    else setExpanded(flip)
  }

  const visibleChildren = (node: PickerNode) =>
    result && result.expand.has(node.key)
      ? node.children.filter((c) => result.visible.has(c.key))
      : node.children

  const roots = tree
    ? result
      ? tree.roots.filter((r) => result.visible.has(r.key))
      : tree.roots
    : []

  const selected = selectedKey && tree ? tree.byKey.get(selectedKey) ?? null : null
  const selectedPath = selected && tree ? ancestorsOf(tree, selected.key) : []
  const selectedStatus =
    selected?.techId != null ? familyStatusByTechId.get(selected.techId) : undefined

  const handleAdd = async () => {
    if (!selected?.techId || adding) return
    setAdding(true)
    setAddError(null)
    try {
      await onAdd(selected.techId)
      setJustAdded(selected.key)
    } catch (e) {
      setAddError((e as Error).message)
    } finally {
      setAdding(false)
    }
  }

  const renderNode = (node: PickerNode, depth: number): ReactNode => {
    const children = visibleChildren(node)
    const hasChildren = node.children.length > 0
    const open = hasChildren && isExpanded(node.key)
    const isDiscipline = node.kind === 'discipline'
    const status = node.techId != null ? familyStatusByTechId.get(node.techId) : undefined
    const match = result?.matches.get(node.key)
    const onRowClick = () => {
      if (isDiscipline) toggleExpand(node.key)
      else {
        setSelectedKey(node.key)
        setAddError(null)
        setJustAdded(null)
      }
    }

    return (
      <div key={node.key}>
        <div
          role="treeitem"
          aria-expanded={hasChildren ? open : undefined}
          aria-selected={selectedKey === node.key}
          tabIndex={0}
          onClick={onRowClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onRowClick()
            } else if (e.key === 'ArrowRight' && hasChildren && !open) {
              toggleExpand(node.key)
            } else if (e.key === 'ArrowLeft' && open) {
              toggleExpand(node.key)
            }
          }}
          className={cn(
            'flex items-center gap-1.5 py-1.5 pr-2 cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
            isDiscipline && 'bg-muted/20',
            selectedKey === node.key && 'bg-accent text-accent-foreground hover:bg-accent',
          )}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              tabIndex={-1}
              aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}
              onClick={(e) => {
                e.stopPropagation()
                toggleExpand(node.key)
              }}
              className="p-0.5 rounded hover:bg-muted shrink-0"
            >
              {open ? (
                <ChevronDown className="size-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-3.5 text-muted-foreground" />
              )}
            </button>
          ) : (
            <span className="w-[18px] shrink-0" />
          )}
          <span
            className={cn(
              'flex-1 min-w-0 truncate text-sm',
              isDiscipline ? 'font-medium text-foreground' : 'text-foreground',
              result && !match && 'text-muted-foreground',
            )}
          >
            <Highlighted text={node.name} tokens={match ? tokens : []} />
          </span>
          {match === 'description' && (
            <span className="shrink-0 text-[10px] text-muted-foreground">in description</span>
          )}
          {status && (
            <Badge variant={STATUS_BADGE[status].variant} className="shrink-0 px-1.5 py-0 text-[10px]">
              {STATUS_BADGE[status].label}
            </Badge>
          )}
          {node.subtreeProductCount > 0 && (
            <span
              className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
              title="Products in this branch"
            >
              {node.subtreeProductCount}
            </span>
          )}
        </div>
        {open && children.map((child) => renderNode(child, depth + 1))}
      </div>
    )
  }

  const matchCount = result
    ? [...result.matches.keys()].filter((k) => k.startsWith('t:')).length
    : 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[1040px] rounded-lg border border-border"
      role="dialog"
      aria-modal="true"
      aria-label="Search existing Tech Tree"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-medium text-foreground">Search existing Tech Tree</h2>
            <p className="text-xs text-muted-foreground">
              Browse disciplines and expand branches, or search to find where a technology
              lives. Select one to add it as a solution family.
            </p>
          </div>
          <Button variant="secondary" size="icon" onClick={onClose} aria-label="Close">
            <X className="size-4" />
          </Button>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search technologies, e.g. fixed wing drones"
              aria-label="Search technologies"
              className="pl-9"
            />
          </div>
          {/* Always rendered so the layout doesn't jump as the user types. */}
          <span className="min-h-4 text-xs text-muted-foreground">
            {result &&
              (matchCount === 0
                ? 'No technologies match.'
                : `${matchCount} ${matchCount === 1 ? 'technology matches' : 'technologies match'}`)}
          </span>
        </div>

        <div className="flex flex-col gap-4 md:flex-row">
          <div
            role="tree"
            aria-label="Technology tree"
            className="h-[55vh] flex-1 min-w-0 overflow-y-auto rounded-md border border-border"
          >
            {loadError ? (
              <p className="p-4 text-sm text-destructive">{loadError}</p>
            ) : !tree ? (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading tech tree…
              </div>
            ) : roots.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                {result ? 'Nothing in the tech tree matches that search.' : 'The tech tree is empty.'}
              </p>
            ) : (
              roots.map((root) => renderNode(root, 0))
            )}
          </div>

          <aside className="flex w-full shrink-0 flex-col gap-3 rounded-md border border-border p-4 md:w-80">
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Select a technology to see its details.
              </p>
            ) : (
              <>
                {selectedPath.length > 0 && (
                  <p className="text-xs text-muted-foreground break-words">
                    {selectedPath.map((n) => n.name).join(' › ')}
                  </p>
                )}
                <h3 className="text-base font-medium text-foreground break-words">
                  {selected.name}
                </h3>
                <div className="flex gap-6 text-xs text-muted-foreground">
                  <span>
                    <span className="text-foreground">{selected.subtreeProductCount}</span> products
                  </span>
                  <span>
                    <span className="text-foreground">{selected.children.length}</span> subtechnologies
                  </span>
                </div>
                {selected.description && (
                  <p className="max-h-48 overflow-y-auto whitespace-pre-wrap text-xs leading-normal text-muted-foreground">
                    {selected.description}
                  </p>
                )}
                <div className="mt-auto flex flex-col gap-2 pt-2">
                  {selectedStatus === 'accepted' ? (
                    <p className="text-sm text-success">
                      {justAdded === selected.key
                        ? 'Added to solution families.'
                        : 'Already one of this program’s solution families.'}
                    </p>
                  ) : (
                    <Button
                      onClick={handleAdd}
                      disabled={adding || !!addDisabledReason}
                      title={addDisabledReason ?? undefined}
                    >
                      {adding ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                      {selectedStatus ? 'Accept as solution family' : 'Add as solution family'}
                    </Button>
                  )}
                  {addDisabledReason && selectedStatus !== 'accepted' && (
                    <p className="text-xs text-muted-foreground">{addDisabledReason}</p>
                  )}
                  {addError && <p className="text-xs text-destructive">{addError}</p>}
                </div>
              </>
            )}
          </aside>
        </div>
      </div>
    </Modal>
  )
}

export default TechTreePickerModal
